// Guia "Como usar" (texto 08): carrega o conteúdo do idioma do app, resolve os textos e monta a busca.
// O CONTEÚDO fica em locales/guia/<idioma>.json (formato em lib/guia/tipos.ts). Sem o arquivo do idioma, vale o pt-BR.
//   {t:Novo fichário} → t('Novo fichário'): o nome REAL do botão, igual ao que a pessoa vê (C6).
//   **negrito**       → destaque.
import type { AbaId, ModId } from '../../store/types';
import { MOD_IDS } from '../../store/types';
import { idioma, t, type Idioma } from '../i18n';
import { norm } from '../date';
import { temPartes } from '../partes';
import { ABAS, GRUPOS, aba } from '../../components/theme';
import type { ConteudoGuia, Pagina, PaginaAba, Passo, Pergunta, Secao } from './tipos';

// ─── Arquivos de cada idioma (os outros entram aqui quando forem traduzidos) ───
const ARQUIVOS: Partial<Record<Idioma, () => unknown>> = {
  'pt-BR': () => require('../../locales/guia/pt-BR.json'),
};

const VAZIO: ConteudoGuia = {
  versao: 0,
  gestos: { id: 'gestos', titulo: '', secoes: [] },
  primeirosPassos: { id: 'primeiros', titulo: '', secoes: [] },
  recursos: [], perguntas: [], abas: {}, descricoes: {},
};

/** Deixa o conteúdo sempre no formato certo (arquivo incompleto ou ainda em construção não derruba a tela). */
function arrumar(x: unknown): ConteudoGuia {
  const c = (x && typeof x === 'object' ? x : {}) as Partial<ConteudoGuia>;
  const pag = (p: Pagina | undefined, id: string): Pagina => ({ id: p?.id || id, titulo: p?.titulo ?? '', icone: p?.icone, secoes: (p?.secoes ?? []).map(sec) });
  const sec = (s: Secao): Secao => ({ ...s, passos: s.passos ?? undefined, perguntas: s.perguntas ?? undefined });
  const abas: ConteudoGuia['abas'] = {};
  for (const [id, a] of Object.entries(c.abas ?? {}) as [AbaId, PaginaAba | undefined][]) {
    if (!a) continue;
    abas[id] = { ...a, aba: id, descricao: a.descricao ?? '', paraQueServe: a.paraQueServe ?? '', primeirosPassos: a.primeirosPassos ?? [],
      partes: (a.partes ?? []).map(sec), dicas: a.dicas ?? [], perguntas: a.perguntas ?? [] };
  }
  return {
    versao: c.versao ?? 0,
    gestos: pag(c.gestos, 'gestos'),
    primeirosPassos: pag(c.primeirosPassos, 'primeiros'),
    recursos: (c.recursos ?? []).map((p, i) => pag(p, 'recurso' + i)),
    perguntas: c.perguntas ?? [],
    abas,
    descricoes: c.descricoes ?? {},
  };
}

let cache: { lang: Idioma; c: ConteudoGuia } | null = null;
/** O conteúdo do guia no idioma do app (sem o arquivo do idioma, o pt-BR). */
export function conteudoGuia(): ConteudoGuia {
  const lang = idioma();
  if (cache?.lang === lang) return cache.c;
  let c: ConteudoGuia = VAZIO;
  for (const l of [lang, 'pt-BR' as Idioma]) {
    const f = ARQUIVOS[l];
    if (!f) continue;
    try { c = arrumar(f() as ConteudoGuia); break; } catch { /* arquivo com problema: tenta o pt-BR */ }
  }
  cache = { lang, c };
  return c;
}

// ─── Texto rico ───
export type Segmento = { texto: string; negrito?: boolean; botao?: boolean };

/** "Toque em {t:Novo fichário} e **pronto**" → segmentos (botão com o nome traduzido do app, negrito). */
export function rico(s: string | undefined): Segmento[] {
  if (!s) return [];
  const out: Segmento[] = [];
  // primeiro os botões {t:...}, depois o **negrito** dentro de cada pedaço de texto
  const re = /\{t:([^}]+)\}/g;
  let i = 0, m: RegExpExecArray | null;
  const texto = (x: string) => {
    const partes = x.split('**');
    partes.forEach((p, k) => { if (p) out.push(k % 2 === 1 ? { texto: p, negrito: true } : { texto: p }); });
  };
  while ((m = re.exec(s))) {
    if (m.index > i) texto(s.slice(i, m.index));
    out.push({ texto: t(m[1].trim()), botao: true });
    i = m.index + m[0].length;
  }
  if (i < s.length) texto(s.slice(i));
  return out;
}
/** Texto puro (busca, balão, leitores de tela). */
export const plano = (s: string | undefined) => rico(s).map(x => x.texto).join('');

// ─── Páginas ───
// Ids das páginas: 'gestos', 'primeiros', 'perguntas' (ficam na própria tela Como usar), 'recurso:<id>', 'aba:<abaId>'.
export const idRecurso = (id: string) => 'recurso:' + id;
export const idAba = (id: AbaId) => 'aba:' + id;
export const abaDaPagina = (pid: string): AbaId | null => (pid.startsWith('aba:') && ABAS.some(a => a.id === pid.slice(4)) ? (pid.slice(4) as AbaId) : null);

/** A aba tem "Partes desta aba"? (só as abas de fichas) */
export const abaTemPartes = (id: AbaId) => (MOD_IDS as string[]).includes(id) && temPartes(id as ModId);

/** Ids fixos das seções da página de aba (modelo B2). */
export const SECOES_ABA = { serve: 'serve', primeiros: 'primeiros', dicas: 'dicas', ligar: 'ligar', perguntas: 'perguntas' } as const;

/** Descrição curta da aba (lista do Guia das abas): a do guia; sem ela, a do app. */
export const descricaoAba = (id: AbaId) => conteudoGuia().descricoes[id] || conteudoGuia().abas[id]?.descricao || aba(id).desc;

/** A página de uma aba no modelo B2, como seções (ordem fixa). Sem conteúdo: só "Para que serve" (o aviso "Guia em construção" fica na tela). */
export function paginaDaAba(id: AbaId): { pagina: Pagina; pronta: boolean } {
  const a = conteudoGuia().abas[id];
  const info = aba(id);
  // texto 29 (item 5): Anotações no formato "Quero… → faça…" (o mesmo texto no ⓘ, no "Como usar esta aba" e em Ajustes)
  if (a?.formato === 'quero') return { pronta: true, pagina: { id: idAba(id), titulo: info.nome, icone: info.icon, formato: 'quero',
    cabecalho: a.titulo, rodape: a.dica, secoes: a.partes.map((s, i) => ({ ...s, aberta: i === 0 })) } };
  if (!a) return { pronta: false, pagina: { id: idAba(id), titulo: info.nome, icone: info.icon,
    secoes: [{ id: SECOES_ABA.serve, titulo: t('Para que serve'), aberta: true, texto: descricaoAba(id) }] } };
  const secoes: Secao[] = [
    { id: SECOES_ABA.serve, titulo: t('Para que serve'), aberta: true, texto: a.paraQueServe || descricaoAba(id) },
    { id: SECOES_ABA.primeiros, titulo: t('Primeiros passos'), passos: a.primeirosPassos },
    ...a.partes,
    { id: SECOES_ABA.dicas, titulo: t('Dicas e atalhos'), passos: a.dicas },
  ];
  if (abaTemPartes(id)) secoes.push({ id: SECOES_ABA.ligar, titulo: t('Ligar e desligar partes'), passos: a.ligarPartes ?? [] });
  secoes.push({ id: SECOES_ABA.perguntas, titulo: t('Perguntas comuns'), perguntas: a.perguntas });
  return { pronta: true, pagina: { id: idAba(id), titulo: info.nome, icone: info.icon, secoes } };
}

/** Página pelo id (para Ajustes abrir e para o título). */
export function paginaGuia(pid: string): Pagina | null {
  const c = conteudoGuia();
  if (pid === 'gestos') return c.gestos;
  if (pid === 'primeiros') return c.primeirosPassos;
  const ab = abaDaPagina(pid);
  if (ab) return paginaDaAba(ab).pagina;
  if (pid.startsWith('recurso:')) return c.recursos.find(r => r.id === pid.slice(8)) ?? null;
  return null;
}

/** Passos com ✓ (os de dicas não contam: são atalhos, não tarefas). */
export const passosComMarca = (p: Pagina): Passo[] => (p.formato === 'quero' ? [] : p.secoes).filter(s => s.id !== SECOES_ABA.dicas).flatMap(s => s.passos ?? []);

// ─── Busca (C4) ───
export type Destino = { pagina: string; secao?: string; passo?: string; pergunta?: string };
export type Achado = { caminho: string; texto: string; destino: Destino; icone?: string };
type Item = Achado & { busca: string };

let indice: { lang: Idioma; itens: Item[] } | null = null;
function montarIndice(): Item[] {
  const c = conteudoGuia();
  const itens: Item[] = [];
  const add = (caminho: string[], texto: string, destino: Destino, icone?: string, extra = '') =>
    itens.push({ caminho: caminho.filter(Boolean).join(' > '), texto, destino, icone, busca: norm(`${caminho.join(' ')} ${texto} ${extra}`) });
  const pagina = (p: Pagina, pid: string, raiz: string[], icone?: string) => {
    for (const s of p.secoes) {
      add([...raiz, s.titulo], s.titulo, { pagina: pid, secao: s.id }, icone, plano(s.texto));
      for (const ps of s.passos ?? []) { const x = plano(ps.texto); add([...raiz, s.titulo, x], x, { pagina: pid, secao: s.id, passo: ps.id }, icone); }
      for (const q of s.perguntas ?? []) { const x = plano(q.p); add([...raiz, s.titulo, x], x, { pagina: pid, secao: s.id, pergunta: q.id }, icone, plano(q.r)); }
    }
  };
  pagina(c.gestos, 'gestos', [t('Gestos do app')], '👆');
  pagina(c.primeirosPassos, 'primeiros', [t('Primeiros passos')], '🚀');
  for (const r of c.recursos) pagina(r, idRecurso(r.id), [r.titulo], r.icone);
  for (const q of c.perguntas) { const x = plano(q.p); add([t('Perguntas comuns'), x], x, { pagina: 'perguntas', pergunta: q.id }, '❓', plano(q.r)); }
  for (const [g] of GRUPOS) for (const a of ABAS.filter(x => x.grupo === g)) {
    const { pagina: p, pronta } = paginaDaAba(a.id);
    add([a.nome], a.nome, { pagina: idAba(a.id) }, a.icon, descricaoAba(a.id));
    if (pronta) pagina(p, idAba(a.id), [a.nome], a.icon);
  }
  return itens;
}

/** Procura em títulos de seções, passos e perguntas de TODAS as páginas. Caminho: "Estudos > Páginas > Mudar o tema". */
export function buscarNoGuia(q: string, max = 40): Achado[] {
  const n = norm(q.trim());
  if (!n) return [];
  const lang = idioma();
  if (indice?.lang !== lang) indice = { lang, itens: montarIndice() };
  const palavras = n.split(/\s+/).filter(Boolean);
  // primeiro o que tem a palavra no próprio título/passo; depois o que só tem no caminho ou na resposta
  const peso = (i: Item) => (palavras.every(w => norm(i.texto).includes(w)) ? 0 : 1);
  return indice.itens.filter(i => palavras.every(w => i.busca.includes(w)))
    .map((i, k) => ({ i, k, p: peso(i) })).sort((a, b) => a.p - b.p || a.k - b.k)
    .slice(0, max).map(({ i: { busca: _b, ...r } }) => r);
}

export type { ConteudoGuia, Pagina, PaginaAba, Passo, Pergunta, Secao };
